import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { tangentBasis } from '@/frames/PlanetaryCoordinates';
import { HeightField } from '@/planet/HeightField';

export interface CharacterInput {
  /** Local movement demand: x = strafe, z = forward, both -1..1. */
  move: Vec3;
  /** Yaw and pitch look deltas, radians. */
  lookYaw: number;
  lookPitch: number;
  jump: boolean;
  sprint: boolean;
  crouch: boolean;
}

export interface CharacterState {
  /** Position in the body-fixed frame, metres. */
  position: Vec3;
  /** Velocity in the body-fixed frame, m/s. */
  velocity: Vec3;
  /** Heading about the local up axis, radians. */
  heading: number;
  /** Look pitch, radians, clamped to just short of vertical. */
  pitch: number;
  grounded: boolean;
  /** Height above the local surface, metres. */
  altitude: number;
  /** Ground slope beneath the character, radians. */
  slope: number;
}

/**
 * Character controller for on-foot exploration.
 *
 * The important property is that there is no global "up". Local up is derived
 * from the character's own position:
 *
 *     localUp = normalize(position - planetCentre)
 *
 * so walking far enough around a body rotates the player's world smoothly and
 * without any special case at the poles or at a chunk boundary. Gravity
 * magnitude comes from the body, so the same controller gives a bounding,
 * long-hangtime walk on Lume at 1.4 m/s^2 and a laboured trudge on Orphys at
 * 22 m/s^2, with no per-body tuning.
 *
 * Collision is resolved against the terrain height function directly rather
 * than against a generated collider mesh. That is exact (it is the same
 * function the mesh is built from), costs nothing to stream, and cannot
 * desynchronise from the visual surface.
 */
export class CharacterController {
  readonly state: CharacterState = {
    position: new Vec3(),
    velocity: new Vec3(),
    heading: 0,
    pitch: 0,
    grounded: false,
    altitude: 0,
    slope: 0,
  };

  /** Eye height above the feet, metres. */
  eyeHeight = 1.68;
  /** Capsule radius, metres. */
  radius = 0.4;
  /** Base walking speed, m/s. */
  walkSpeed = 4.2;
  sprintMultiplier = 1.9;
  crouchMultiplier = 0.45;
  /** Jump height on a 1 g world, metres. Scaled by local gravity. */
  jumpHeight = 0.55;
  /** Ground acceleration, m/s^2. */
  groundAcceleration = 34;
  /** Air control fraction, 0..1. */
  airControl = 0.14;
  /** Maximum walkable slope, radians. */
  maxSlope = 0.85;

  private jumpCooldown = 0;

  constructor(private heightField: HeightField) {}

  setHeightField(field: HeightField): void {
    this.heightField = field;
  }

  /** Places the character on the surface at a planet-fixed direction. */
  spawnAt(direction: Vec3): void {
    const dir = direction.normalized();
    const surface = this.heightField.walkableRadiusAt(dir);
    this.state.position.copy(dir).mulScalar(surface + 0.1);
    this.state.velocity.set(0, 0, 0);
    this.state.grounded = true;
  }

  /** Local up vector at the character's position. */
  get up(): Vec3 {
    return this.state.position.normalized();
  }

  /** Camera orientation in the body-fixed frame. */
  viewOrientation(): Quat {
    const basis = tangentBasis(this.state.position);
    // Heading rotates the forward direction within the tangent plane.
    const cosH = Math.cos(this.state.heading);
    const sinH = Math.sin(this.state.heading);
    const forwardFlat = basis.north.clone().mulScalar(cosH).addScaled(basis.east, sinH).normalize();

    // Pitch tilts the view about the local horizontal.
    const cosP = Math.cos(this.state.pitch);
    const sinP = Math.sin(this.state.pitch);
    const forward = forwardFlat.clone().mulScalar(cosP).addScaled(basis.up, sinP).normalize();
    return Quat.lookRotation(forward, basis.up);
  }

  /** Eye position in the body-fixed frame. */
  eyePosition(out = new Vec3()): Vec3 {
    return out.copy(this.state.position).addScaled(this.up, this.eyeHeight);
  }

  /**
   * Advances the character one fixed step.
   *
   * @param gravityMagnitude local gravitational acceleration, m/s^2
   * @param airDensity local air density, used only for a small drag term
   */
  step(input: CharacterInput, gravityMagnitude: number, airDensity: number, dt: number): void {
    const s = this.state;
    this.jumpCooldown = Math.max(0, this.jumpCooldown - dt);

    // --- Look ---------------------------------------------------------------
    s.heading += input.lookYaw;
    s.pitch = clamp(s.pitch + input.lookPitch, -1.52, 1.52);

    // --- Local frame --------------------------------------------------------
    const basis = tangentBasis(s.position);
    const up = basis.up;
    const cosH = Math.cos(s.heading);
    const sinH = Math.sin(s.heading);
    const forward = basis.north.clone().mulScalar(cosH).addScaled(basis.east, sinH).normalize();
    const right = Vec3.crossVectors(forward, up).normalize();

    // --- Ground query -------------------------------------------------------
    const surfaceRadius = this.heightField.walkableRadiusAt(s.position);
    const radius = s.position.length();
    s.altitude = radius - surfaceRadius;
    const normal = this.heightField.normalAt(s.position, 1.5);
    s.slope = Math.acos(clamp(normal.dot(up), -1, 1));

    const wasGrounded = s.grounded;
    s.grounded = s.altitude <= 0.08 && s.velocity.dot(up) <= 1e-3;

    // --- Movement demand ----------------------------------------------------
    const speedScale =
      (input.sprint ? this.sprintMultiplier : 1) * (input.crouch ? this.crouchMultiplier : 1);
    const desired = right
      .clone()
      .mulScalar(input.move.x)
      .addScaled(forward, input.move.z);
    if (desired.lengthSq() > 1) desired.normalize();
    desired.mulScalar(this.walkSpeed * speedScale);

    // Steep ground cannot be walked up; the character slides instead.
    const tooSteep = s.slope > this.maxSlope;

    // Split velocity into the tangent plane and the vertical.
    const vertical = s.velocity.dot(up);
    const horizontal = s.velocity.rejectFromUnit(up);

    if (s.grounded && !tooSteep) {
      const delta = Vec3.subVectors(desired, horizontal);
      const accel = this.groundAcceleration * dt;
      if (delta.length() > accel) delta.normalize().mulScalar(accel);
      horizontal.add(delta);
      // Friction when no input is given.
      if (desired.lengthSq() < 1e-6) horizontal.mulScalar(Math.exp(-9 * dt));
    } else {
      // Reduced authority in the air; momentum dominates, which is what makes
      // low-gravity jumps feel committal.
      const delta = Vec3.subVectors(desired, horizontal).mulScalar(this.airControl);
      const accel = this.groundAcceleration * this.airControl * dt;
      if (delta.length() > accel) delta.normalize().mulScalar(accel);
      horizontal.add(delta);
    }

    // --- Vertical -----------------------------------------------------------
    let newVertical = vertical;
    if (s.grounded) {
      if (tooSteep) {
        // Slide down the slope under gravity.
        const slideDir = normal.rejectFromUnit(up);
        if (slideDir.lengthSq() > 1e-9) {
          horizontal.addScaled(slideDir.normalize(), -gravityMagnitude * dt * 0.7);
        }
        newVertical = Math.min(newVertical, 0);
      } else {
        newVertical = Math.max(0, newVertical);
      }

      if (input.jump && this.jumpCooldown <= 0) {
        // v = sqrt(2 g h): the same jump height on every world would be wrong,
        // so the impulse is fixed and the *height* varies with gravity.
        newVertical = Math.sqrt(2 * 9.81 * this.jumpHeight);
        this.jumpCooldown = 0.25;
        s.grounded = false;
      }
    } else {
      newVertical -= gravityMagnitude * dt;
    }

    // A small aerodynamic drag term, meaningful only on dense worlds.
    if (airDensity > 0.01) {
      const drag = clamp01(airDensity * 0.02 * dt);
      horizontal.mulScalar(1 - drag);
      newVertical *= 1 - drag;
    }

    s.velocity.copy(horizontal).addScaled(up, newVertical);
    s.position.addScaled(s.velocity, dt);

    // --- Ground resolution --------------------------------------------------
    const newSurface = this.heightField.walkableRadiusAt(s.position);
    const newRadius = s.position.length();
    if (newRadius < newSurface) {
      const dir = s.position.normalized();
      s.position.copy(dir).mulScalar(newSurface);
      const v = s.velocity.dot(dir);
      if (v < 0) s.velocity.addScaled(dir, -v);
      s.grounded = true;
      if (!wasGrounded) {
        // Landing: absorb some horizontal energy.
        s.velocity.mulScalar(0.82);
      }
    }
    s.altitude = s.position.length() - newSurface;
  }

  /** Fall damage estimate from the last landing, 0..1. */
  impactSeverity(gravityMagnitude: number): number {
    const impact = Math.abs(Math.min(0, this.state.velocity.dot(this.up)));
    // Survivable fall speed scales with gravity: a fall that is fatal on Orphys
    // is a hop on Lume.
    const tolerance = 9 * Math.sqrt(9.81 / Math.max(0.05, gravityMagnitude));
    return clamp01((impact - tolerance) / Math.max(1, tolerance));
  }
}
